import { notification } from '@retail-core/rds';
import { getHosts } from './environment';

function getToken(): string {
  const match = document.cookie.match(/RETAIL_ACCESS_TOKEN=([^;$]+)/);
  if (!match) {
    throw new Error('Login required');
  }
  return match[1];
}

export class UserAbortedError implements Error {
  message: string;
  name: string;

  constructor() {
    this.name = 'UserAbortedError';
    this.message = 'The request is cancelled by the user';
  }
}

export class RestApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(`REST API Error (${status}): ${message}`);
    // Required for correct instanceof checks when extending built-in Error with Babel/tsc targets below ES2022
    Object.setPrototypeOf(this, RestApiError.prototype);
    this.name = 'RestApiError';
    this.status = status;
  }
}

const httpStatusDesc: Record<number, string> = {
  0: 'Network error',
  400: 'Bad Request',
  401: 'Logged out',
  403: 'Permission denied',
  404: 'Not Found',
  500: 'Internal Server Error',
  504: 'Gateway timeout',
};

async function handleRestError(error: Response | DOMException): Promise<Error> {
  if (error instanceof DOMException) {
    return new UserAbortedError();
  }

  const status = error.status;
  let errorMessage = httpStatusDesc[status] || 'Unknown error';
  let responseBody: any;

  try {
    responseBody = await error.json();
    if (responseBody.message) {
      errorMessage = responseBody.message;
    } else if (responseBody.detail) {
      errorMessage = responseBody.detail;
    }
  } catch {
    // If response is not JSON, use status text
    errorMessage = error.statusText || errorMessage;
  }

  // Don't show notification for 404 errors (handled in calling code)
  if (status !== 404) {
    notification.error({
      message: 'There was an error on this page (sizing)',
      description: errorMessage,
      duration: 0,
      placement: 'topRight',
    });
  }

  return new RestApiError(status, errorMessage);
}

export type RestMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

type RestApiOptions = {
  contentType?: string;
};

/**
 * REST API client for presize-backend
 * @param method HTTP method (GET, POST, PATCH, etc.)
 * @param endpoint API endpoint path (e.g., '/api/orders')
 * @param payload Request body (for POST/PATCH/PUT)
 * @param queryParams Query string parameters (for GET requests)
 * @param signal AbortSignal for request cancellation
 * @param referenceId Order reference ID for X-Reference-Id header
 */
export function restApi<D>(
  method: RestMethod,
  endpoint: string,
  payload?: any,
  queryParams?: Record<string, string | number | boolean | undefined | null>,
  signal?: AbortSignal,
  referenceId?: string,
  optionsOrContentType?: RestApiOptions | string
): Promise<D> {
  // CORS Fix: Use relative URL on localhost so webpack proxy works
  // In production (embedded in Buying Home), use environment-aware PRESIZE_BACKEND_HOST
  const isLocalhost = window.location.hostname.includes('localhost');
  let url = isLocalhost
    ? `/presize${endpoint}` // Relative URL → webpack proxy → Presize Backend
    : `${getHosts().PRESIZE_BACKEND_HOST}${endpoint}`; // Absolute URL → Presize Backend

  // Debug logging to see actual URL being called
  console.log(`🔵 [REST API] ${method} ${url} (isLocalhost: ${isLocalhost})`);

  // Build query string for GET requests
  if (method === 'GET' && queryParams) {
    const params = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });
    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  const resolvedContentType =
    typeof optionsOrContentType === 'string'
      ? optionsOrContentType
      : (optionsOrContentType?.contentType ?? 'application/json');

  const headers: HeadersInit = {
    Authorization: `Bearer ${getToken()}`,
    'Content-Type': resolvedContentType,
  };

  // Add X-Reference-Id header if provided
  if (referenceId) {
    headers['X-Reference-Id'] = referenceId;
  }

  const requestOptions: RequestInit = {
    method,
    headers,
    signal,
  };

  // Add body for non-GET requests
  if (method !== 'GET' && payload) {
    requestOptions.body = JSON.stringify(payload);
  }

  return fetch(url, requestOptions)
    .then(async (response) => {
      console.log(`🟢 [REST API] ← ${response.status} ${response.statusText} from ${url}`);

      if (!response.ok) {
        throw response;
      }

      // Handle empty responses (e.g., 204 No Content)
      const contentType = response.headers.get('Content-Type');
      if (!contentType || !contentType.includes('json')) {
        return {} as D;
      }

      return response.json() as Promise<D>;
    })
    .catch(async (error) => {
      console.log(`🔴 [REST API] ERROR:`, error);
      throw await handleRestError(error);
    });
}
